import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import Navbar from "../Layout/Navbar";

const theme = createTheme({
  palette: {
    primary: {
      main: "#d32f2f",
    },
    secondary: {
      main: "#ff6b35",
    },
  },
  breakpoints: {
    values: {
      xs: 360,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const NhapHangNL = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [phieuNhapList, setPhieuNhapList] = useState([]);
  const [filteredPhieuNhapList, setFilteredPhieuNhapList] = useState([]);
  const [stores, setStores] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [userRole, setUserRole] = useState("user");
  const [userInfo, setUserInfo] = useState({});
  const [sortByStatus, setSortByStatus] = useState(false);
  const [sortByStore, setSortByStore] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tripFilter, setTripFilter] = useState("all");
  const [showAll, setShowAll] = useState(false); // Add showAll state
  const [fromDate, setFromDate] = useState(new Date()); // Add fromDate state
  const [toDate, setToDate] = useState(new Date()); // Add toDate state
  const navigate = useNavigate();

  // Determine user role from localStorage
  const getUserRole = () => {
    const maPB = localStorage.getItem("MaPB");
    const isAdmin = localStorage.getItem("isAdmin");
    const userMaCH = localStorage.getItem("IDCH");
    const userIDCH = localStorage.getItem("IDCH");

    const userInfo = {
      maPB: maPB,
      isAdmin: isAdmin === "true",
      maCH: userMaCH,
      idCH: userIDCH,
    };
    console.log("User Info:", userInfo);
    setUserInfo(userInfo);

    // Admin permissions
    if (userInfo.isAdmin && ["4", "321", "145", "170", "187", "250"].includes(maPB)) {
      return "admin";
    }

    // Ban điều phối permissions
    if (["80", "53", "4"].includes(maPB)) {
      return "coordinator";
    }

    // Regular user
    return "user";
  };

  // Add function to fetch stores
  const fetchStores = async () => {
    try {
      const response = await axios.get("https://doticom.vn:3000/stores");
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  };

  // Add function to get store name by ID
  const getStoreName = (storeId) => {
    const store = stores.find((store) => store.IDCH === storeId);
    return store ? store.TenCuaHang : storeId;
  };

  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    fetchStores(); // Fetch stores when component mounts
  }, []);

  useEffect(() => {
    // Only fetch data when we have both userRole and userInfo properly set
    if (userRole && (userRole !== "user" || userInfo.idCH)) {
      if (showAll) {
        fetchPhieuNhapAll();
      } else {
        fetchPhieuNhap();
      }
    }
  }, [selectedDate, userRole, userInfo, showAll]);

  const sortPhieuByStatus = (data) => {
    if (!sortByStatus) return data;

    const statusOrder = {
      "Đã nhận, có chênh lệch": 1, // Moved to top priority
      "Đã gửi phiếu": 2,
      "Cửa hàng đã xác nhận": 3,
      "Đã duyệt": 4,
    };

    return data.sort((a, b) => {
      const orderA = statusOrder[a.trangThai] || 999;
      const orderB = statusOrder[b.trangThai] || 999;
      return orderA - orderB;
    });
  };

  const sortPhieuByStore = (data) => {
    if (!sortByStore) return data;

    return data.sort((a, b) => {
      // First sort by store name
      const storeNameA = getStoreName(a.khachHang);
      const storeNameB = getStoreName(b.khachHang);
      const storeComparison = storeNameA.localeCompare(storeNameB);
      
      if (storeComparison !== 0) {
        return storeComparison;
      }
      
      // If same store, sort by trip number (chuyen)
      return a.chuyen - b.chuyen;
    });
  };

  // Add function to filter by status
  const filterPhieuByStatus = (data, filter) => {
    if (filter === "all") return data;
    return data.filter((phieu) => phieu.trangThai === filter);
  };

  // Add function to assign trip numbers
  const assignTripNumbers = (data) => {
    // Group by store
    const storeGroups = data.reduce((groups, phieu) => {
      const storeId = phieu.khachHang;
      if (!groups[storeId]) {
        groups[storeId] = [];
      }
      groups[storeId].push(phieu);
      return groups;
    }, {});

    // Assign trip numbers for each store
    Object.keys(storeGroups).forEach((storeId) => {
      // Sort by receipt number (soPhieu) for each store
      storeGroups[storeId].sort((a, b) => {
        if (typeof a.soPhieu === "string" && typeof b.soPhieu === "string") {
          return a.soPhieu.localeCompare(b.soPhieu);
        }
        return a.soPhieu - b.soPhieu;
      });

      // Assign trip numbers
      storeGroups[storeId].forEach((phieu, index) => {
        phieu.chuyen = index + 1;
      });
    });

    return data;
  };

  const filterPhieuByTrip = (data, filter) => {
    if (filter === "all") return data;
    return data.filter((phieu) => phieu.chuyen === parseInt(filter));
  };

  // Update the effect to apply sorting and filtering
  useEffect(() => {
    let processedList = [...phieuNhapList];

    // Filter by date range when showAll is true
    if (showAll && fromDate && toDate) {
      const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
      
      processedList = processedList.filter((phieu) => {
        const phieuDate = new Date(phieu.ngay);
        const phieuDateOnly = new Date(phieuDate.getFullYear(), phieuDate.getMonth(), phieuDate.getDate());
        return phieuDateOnly >= fromDateOnly && phieuDateOnly <= toDateOnly;
      });
    }

    // Apply sorting if enabled
    if (sortByStatus) {
      processedList = sortPhieuByStatus(processedList);
    } else if (sortByStore) {
      processedList = sortPhieuByStore(processedList);
    }

    // Apply filters
    processedList = filterPhieuByStatus(processedList, statusFilter);
    processedList = filterPhieuByTrip(processedList, tripFilter);

    setFilteredPhieuNhapList(processedList);
  }, [phieuNhapList, sortByStatus, sortByStore, statusFilter, tripFilter, showAll, fromDate, toDate]);

  const fetchPhieuNhap = async () => {
    try {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      let apiUrl = "https://doticom.vn:3000/api/sale-nhap-hang-admin";
      let params = { NgayNhap: formattedDate };

      // Add filtering based on user role (remove user role API filtering)
      if (userRole === "coordinator") {
        params.CreatedBy = userInfo.maPB; // Assuming coordinator can only see their created receipts
      }

      const response = await axios.get(apiUrl, { params });
      let data = response.data
        .filter((phieu) => phieu.LoaiPhieu === 78) // Filter for LoaiPhieu = 78
        .map((phieu) => {
          // Check if there's a discrepancy between ordered and received quantities
          // Only check items that have SLNhapDau > 0 and SLThucNhan is defined
          const hasDiscrepancy = phieu.Items.some(
            (item) =>
              item.SLNhapDau !== item.SLThucNhan &&
              item.SLNhapDau &&
              item.SLNhapDau > 0 &&
              item.SLThucNhan !== null &&
              item.SLThucNhan !== undefined
          );

          // Determine status based on conditions
          let trangThai;
          if (phieu.Act_phieu) {
            trangThai = "Đã duyệt";
          } else if (phieu.Status === 1 && hasDiscrepancy) {
            trangThai = "Đã nhận, có chênh lệch";
          } else if (phieu.Status === 1) {
            trangThai = "Cửa hàng đã xác nhận";
          } else {
            trangThai = "Đã gửi phiếu";
          }

          return {
            id: phieu.Phieu,
            soPhieu: phieu.SoPN,
            ngay: new Date(phieu.NgayNhap),
            khachHang: phieu.MaCH,
            trangThai: trangThai,
            tongSoLuong: phieu.Items.reduce((sum, item) => sum + item.SLNhapDau, 0),
            items: phieu.Items,
            createdBy: phieu.CreatedBy,
            isApproved: phieu.Act_phieu,
            loaiPhieu: phieu.LoaiPhieu,
          };
        });

      // Remove the existing filter that excludes LoaiPhieu 77 since we now only want 78
      // data = data.filter((phieu) =>
      //   !(phieu.ghiChu && phieu.ghiChu.includes("Nhập bánh trung thu"))
      //   && phieu.loaiPhieu !== 77
      // );

      console.log("Raw data:", data);
      console.log("User role:", userRole);
      console.log("User IDCH:", userInfo.maCH);

      // Filter data for user role based on IDCH from localStorage
      if (userRole === "user" && userInfo.idCH) {
        const originalLength = data.length;
        data = data.filter((phieu) => {
          const match = String(phieu.khachHang) === String(userInfo.idCH);
          console.log(
            `Comparing phieu.khachHang: ${phieu.khachHang
            } (${typeof phieu.khachHang}) with userInfo.idCH: ${userInfo.idCH
            } (${typeof userInfo.idCH}) - Match: ${match}`
          );
          return match;
        });
        console.log(
          `Filtered from ${originalLength} to ${data.length} records`
        );
      }

      const sortedData = data.sort((a, b) => {
        if (typeof a.soPhieu === "string" && typeof b.soPhieu === "string") {
          return a.soPhieu.localeCompare(b.soPhieu);
        }
        return a.soPhieu - b.soPhieu;
      });

      // Assign trip numbers after sorting
      const dataWithTrips = assignTripNumbers(sortedData);
      setPhieuNhapList(dataWithTrips);
    } catch (error) {
      console.error("Error fetching phiếu nhập:", error);
      setSnackbarMessage("Có lỗi khi tải dữ liệu phiếu nhập");
      setSnackbarOpen(true);
    }
  };

  const fetchPhieuNhapAll = async (fromDateParam = null, toDateParam = null) => {
    try {
      let apiUrl = "https://doticom.vn:3000/api/sale-nhap-hang-admin-all-nguyenlieu";
      let params = {};

      // Add filtering based on user role
      if (userRole === "coordinator") {
        params.CreatedBy = userInfo.maPB;
      }

      // Add date range parameters if provided
      if (fromDateParam && toDateParam) {
        params.fromDate = format(fromDateParam, "yyyy-MM-dd");
        params.toDate = format(toDateParam, "yyyy-MM-dd");
      }

      const response = await axios.get(apiUrl, { params });
      let data = response.data
        .filter((phieu) => phieu.LoaiPhieu === 78) // Filter for LoaiPhieu = 78
        .map((phieu) => {
          // Check if there's a discrepancy between ordered and received quantities
          // Only check items that have SLNhapDau > 0 and SLThucNhan is defined
          const hasDiscrepancy = phieu.Items.some(
            (item) =>
              item.SLNhapDau !== item.SLThucNhan &&
              item.SLNhapDau &&
              item.SLNhapDau > 0 &&
              item.SLThucNhan !== null &&
              item.SLThucNhan !== undefined
          );

          // Determine status based on conditions
          let trangThai;
          if (phieu.Act_phieu) {
            trangThai = "Đã duyệt";
          } else if (phieu.Status === 1 && hasDiscrepancy) {
            trangThai = "Đã nhận, có chênh lệch";
          } else if (phieu.Status === 1) {
            trangThai = "Cửa hàng đã xác nhận";
          } else {
            trangThai = "Đã gửi phiếu";
          }

          return {
            id: phieu.Phieu,
            soPhieu: phieu.SoPN,
            ngay: new Date(phieu.NgayNhap),
            khachHang: phieu.MaCH,
            trangThai: trangThai,
            tongSoLuong: phieu.Items.reduce((sum, item) => sum + item.SLNhapDau, 0),
            items: phieu.Items,
            createdBy: phieu.CreatedBy,
            isApproved: phieu.Act_phieu,
            loaiPhieu: phieu.LoaiPhieu,
          };
        });

      console.log("Raw data (all):", data);
      console.log("User role:", userRole);
      console.log("User IDCH:", userInfo.maCH);

      // Filter data for user role based on IDCH from localStorage
      if (userRole === "user" && userInfo.idCH) {
        const originalLength = data.length;
        data = data.filter((phieu) => {
          const match = String(phieu.khachHang) === String(userInfo.idCH);
          console.log(
            `Comparing phieu.khachHang: ${phieu.khachHang
            } (${typeof phieu.khachHang}) with userInfo.idCH: ${userInfo.idCH
            } (${typeof userInfo.idCH}) - Match: ${match}`
          );
          return match;
        });
        console.log(
          `Filtered from ${originalLength} to ${data.length} records`
        );
      }

      const sortedData = data.sort((a, b) => b.ngay - a.ngay);

      // Assign trip numbers after sorting
      const dataWithTrips = assignTripNumbers(sortedData);
      setPhieuNhapList(dataWithTrips);
    } catch (error) {
      console.error("Error fetching phiếu nhập (all):", error);
      setSnackbarMessage("Có lỗi khi tải dữ liệu phiếu nhập (tất cả)");
      setSnackbarOpen(true);
    }
  };

  const handleShowAll = () => {
    setShowAll(true);
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setFromDate(thirtyDaysAgo);
    setToDate(today);
    fetchPhieuNhapAll(thirtyDaysAgo, today);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setShowAll(false); // Return to single day view when date is changed
  };

  // Add new function to handle date range filtering
  const handleDateRangeFilter = () => {
    if (fromDate && toDate) {
      fetchPhieuNhapAll(fromDate, toDate);
    }
  };

  const handleViewPhieu = async (id) => {
    try {
      const response = await axios.get(
        `https://doticom.vn:3000/api/sale-nhap-hang-detail/${id}`
      );
      const selectedPhieu = response.data;
      navigate(`/phieu-nhap-nguyen-lieu/${id}`, { state: { phieu: selectedPhieu } });
    } catch (error) {
      console.error("Error fetching phiếu nhập details:", error);
      setSnackbarMessage("Có lỗi khi tải chi tiết phiếu nhập");
      setSnackbarOpen(true);
    }
  };

  const handleAddPhieu = () => {
    if (userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền thêm phiếu nhập");
      setSnackbarOpen(true);
      return;
    }
    navigate("/new-phieu-nhap-nguyen-lieu");
  };

  const handleEditPhieu = async (id) => {
    if (userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền chỉnh sửa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await axios.get(
        `https://doticom.vn:3000/api/sale-nhap-hang-detail/${id}`
      );
      const selectedPhieu = response.data;
      navigate(`/new-phieu-nhap-nguyen-lieu/${id}/edit`, { state: { phieu: selectedPhieu } });
    } catch (error) {
      console.error("Error fetching phiếu nhập details for editing:", error);
      setSnackbarMessage("Có lỗi khi tải chi tiết phiếu nhập để chỉnh sửa");
      setSnackbarOpen(true);
    }
  };

  const handlePrintPhieu = (id) => {
    if (userRole !== "admin") {
      setSnackbarMessage("Bạn không có quyền in phiếu nhập");
      setSnackbarOpen(true);
      return;
    }
    window.open(`/phieu-nhap/${id}`, "_blank");
  };

  const handleDelete = async (id) => {
    const phieu = phieuNhapList.find((p) => p.id === id);

    if (userRole === "user") {
      setSnackbarMessage("Bạn không có quyền xóa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    if (userRole === "coordinator" && phieu.isApproved) {
      setSnackbarMessage("Phiếu đã được duyệt, cần admin phê duyệt để xóa");
      setSnackbarOpen(true);
      return;
    }

    if (userRole !== "admin" && userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền xóa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    try {
      await axios.delete(
        `https://doticom.vn:3000/api/sale-nhap-hang/${id}`
      );

      const updatedPhieuNhapList = phieuNhapList.filter(
        (phieu) => phieu.id !== id
      );
      setPhieuNhapList(updatedPhieuNhapList);

      setSnackbarMessage("Đã xóa phiếu nhập thành công");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting phiếu nhập:", error);
      setSnackbarMessage("Có lỗi khi xóa phiếu nhập");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (date) => {
    return format(date, "dd/MM/yyyy", { locale: vi });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Đã duyệt":
        return "#4CAF50";
      case "Đã nhận, có chênh lệch":
        return "#FF9800";
      case "Cửa hàng đã xác nhận":
        return "#2196F3";
      case "Đã gửi phiếu":
        return "#FFC107";
      case "Đang giao":
        return "#2196F3";
      case "Đã hủy":
        return "#F44336";
      default:
        return "#757575";
    }
  };

  const handleSortByStatus = () => {
    setSortByStatus(!sortByStatus);
    if (!sortByStatus) {
      setSortByStore(false); // Disable store sorting when status sorting is enabled
    }
  };

  const handleSortByStore = () => {
    setSortByStore(!sortByStore);
    if (!sortByStore) {
      setSortByStatus(false); // Disable status sorting when store sorting is enabled
    }
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleTripFilterChange = (event) => {
    setTripFilter(event.target.value);
  };

  // Get available trip numbers for filter
  const getAvailableTrips = () => {
    const trips = [...new Set(phieuNhapList.map((phieu) => phieu.chuyen))].sort(
      (a, b) => a - b
    );
    return trips;
  };

  return (
    <ThemeProvider theme={theme}>
      <Navbar />
      <Container
        maxWidth="xl"
        sx={{
          mt: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "#d32f2f",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              }}
            >
              Danh Sách Phiếu Nhập Hàng
            </Typography>

            {userRole === "coordinator" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddPhieu}
                sx={{
                  borderRadius: 8,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Thêm Phiếu
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mr: { sm: 2 },
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              Chọn ngày:
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={vi}
            >
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 200 } }}
                  />
                )}
                disabled={showAll}
              />
            </LocalizationProvider>

            <Button
              variant={showAll ? "contained" : "outlined"}
              color="secondary"
              sx={{
                ml: { sm: 2 },
                borderRadius: 8,
                fontWeight: 600,
                fontSize: { xs: "0.85rem", md: "1rem" },
                minWidth: 120,
                boxShadow: showAll ? "0 4px 8px rgba(0,0,0,0.15)" : "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(0,0,0,0.18)",
                },
              }}
              onClick={handleShowAll}
              disabled={showAll}
            >
              Xem tất cả
            </Button>

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                ml: { sm: 1 }
              }}
            >
              <InputLabel>Lọc theo trạng thái</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Lọc theo trạng thái"
              >
                <MenuItem value="all">Tất cả</MenuItem>
                <MenuItem value="Đã gửi phiếu">Đã gửi phiếu</MenuItem>
                <MenuItem value="Cửa hàng đã xác nhận">Cửa hàng đã xác nhận</MenuItem>
                <MenuItem value="Đã nhận, có chênh lệch">Đã nhận, có chênh lệch</MenuItem>
                <MenuItem value="Đã duyệt">Đã duyệt</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 120 },
                ml: { sm: 1 },
              }}
            >
              <InputLabel>Lọc theo chuyến</InputLabel>
              <Select
                value={tripFilter}
                onChange={handleTripFilterChange}
                label="Lọc theo chuyến"
              >
                <MenuItem value="all">Tất cả</MenuItem>
                {getAvailableTrips().map((trip) => (
                  <MenuItem key={trip} value={trip.toString()}>
                    Chuyến {trip}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={sortByStatus ? "contained" : "outlined"}
              onClick={handleSortByStatus}
              sx={{
                ml: { sm: 1 },
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              {sortByStatus ? "Bỏ sắp xếp trạng thái" : "Sắp xếp theo trạng thái"}
            </Button>

            <Button
              variant={sortByStore ? "contained" : "outlined"}
              onClick={handleSortByStore}
              sx={{
                ml: { sm: 1 },
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              {sortByStore ? "Bỏ sắp xếp cửa hàng" : "Sắp xếp theo cửa hàng"}
            </Button>
          </Box>

          {/* Date range picker - only show when showAll is true */}
          {showAll && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                mb: 3,
                gap: { xs: 1, sm: 2 },
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mr: { sm: 2 },
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  fontWeight: "bold",
                }}
              >
                Lọc theo khoảng thời gian:
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={vi}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2">Từ ngày:</Typography>
                  <DatePicker
                    value={fromDate}
                    onChange={(newDate) => setFromDate(newDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 150 } }}
                      />
                    )}
                  />
                  <Typography variant="body2">Đến ngày:</Typography>
                  <DatePicker
                    value={toDate}
                    onChange={(newDate) => setToDate(newDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 150 } }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDateRangeFilter}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    Lọc
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>
          )}

          <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              mb: 3,
              overflowX: "auto",
              "& .MuiTable-root": {
                minWidth: { xs: 750, md: "auto" },
              },
            }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#ffcccb" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 80, md: "auto" },
                    }}
                  >
                    Số Phiếu
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 90, md: "auto" },
                    }}
                  >
                    Ngày
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 100, md: "auto" },
                    }}
                  >
                    Khách Hàng
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 60, md: "auto" },
                    }}
                  >
                    Chuyến
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 90, md: "auto" },
                    }}
                  >
                    Trạng Thái
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 80, md: "auto" },
                    }}
                  >
                    Tổng SL
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      minWidth: { xs: 140, md: "auto" },
                    }}
                  >
                    Thao Tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPhieuNhapList.length > 0 ? (
                  filteredPhieuNhapList.map((phieu) => (
                    <TableRow key={phieu.id} hover>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        {phieu.soPhieu}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        {formatDate(phieu.ngay)}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        {getStoreName(phieu.khachHang)}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "white",
                            bgcolor: "#2196F3",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: "inline-block",
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            minWidth: "40px",
                            textAlign: "center",
                          }}
                        >
                          {phieu.chuyen}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            color: "white",
                            bgcolor: getStatusColor(phieu.trangThai),
                            px: { xs: 1, md: 1.5 },
                            py: 0.5,
                            borderRadius: 1,
                            display: "inline-block",
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                          }}
                        >
                          {phieu.trangThai}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        {phieu.tongSoLuong}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 0.5, sm: 0 },
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Xem phiếu">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewPhieu(phieu.id)}
                              size="small"
                              sx={{ minWidth: { xs: 32, md: "auto" } }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {userRole === "admin" && (
                            <Tooltip title="In phiếu">
                              <IconButton
                                color="secondary"
                                onClick={() => handlePrintPhieu(phieu.id)}
                                size="small"
                                sx={{ minWidth: { xs: 32, md: "auto" } }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {userRole === "coordinator" && (
                            <Tooltip title="Chỉnh sửa">
                              <IconButton
                                onClick={() => handleEditPhieu(phieu.id)}
                                size="small"
                                sx={{ minWidth: { xs: 32, md: "auto" } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {(userRole === "admin" ||
                            userRole === "coordinator") && (
                              <Tooltip title="Xóa">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDelete(phieu.id)}
                                  sx={{ minWidth: { xs: 32, md: "auto" } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography
                        variant="body1"
                        sx={{
                          py: 2,
                          fontSize: { xs: "0.875rem", md: "1rem" },
                        }}
                      >
                        {statusFilter === "all" && tripFilter === "all"
                          ? `Không tìm thấy phiếu nhập hàng nào cho ngày ${formatDate(selectedDate)}`
                          : `Không tìm thấy phiếu nhập hàng nào ${statusFilter !== "all" ? `có trạng thái "${statusFilter}"` : ""} ${tripFilter !== "all" ? `chuyến ${tripFilter}` : ""} cho ngày ${formatDate(selectedDate)}`
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "#666",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Tổng số phiếu: {filteredPhieuNhapList.length}
              {statusFilter !== "all" && ` (lọc trạng thái: ${statusFilter})`}
              {tripFilter !== "all" && ` (lọc chuyến: ${tripFilter})`}
            </Typography>
          </Box>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default NhapHangNL;
